import { Module } from 'vuex';
import * as types from '../mutation-types';
import api from '../../api/modules/products';
import formatProduct from '@/core/store/utils/format-products';
import ProductState from '@/core/typings/enums/ProductState';
import { cloneDeep, flow, pick, sortBy } from 'lodash';
import { LESSON_START_TYPE_AFTER_PREVIOUS_LESSON } from '@/core/utils/products';
import slugify from '@/core/utils/slugify';

interface ProductsFilter {
  q: string | null;
  productState: ProductState | null;
}

interface ProductForm extends CreateProductRequest, UpdateProductRequest {
  cover_file: PhotoResponse;
  thumbnail_file: PhotoResponse;
  content: number[];
  sales_page: ProductSalesPageResponse;
  custom1: string;
  custom2: string;
  custom3: string;
  wish_price_enabled: boolean;
  min_wish_price: number;
}

interface State {
  product: ProductDetailResponse | null;
  productMedia: ProductMediaResponse[] | null;
  productMediaDetail: MediaDetailResponse | null;
  options: FilterCategoryResponse[] | null;
  studioInstructors: InstructorResponse[] | null;
  productMediaNextPage?: string;
  teacherProducts: ProductResponse[] | null;
  teacherProduct: ProductDetailResponse | null;
  teacherCoupons: PaginatedResponse<TeacherCouponsResponse> | null;
  teacherTaxRates: TeacherTaxRateResponse[] | null;
  productTags: TeacherProductTagResponse[] | null;
  teacherCoupon: TeacherCouponsResponse | null;
  teacherCouponsStats: CouponsStatsResponse | null;
  teacherProductsWithPrivateSessions: ProductResponse[] | null;
  teacherProductsFilter: ProductsFilter;
  productForm: ProductForm | null;
  playlists: PlaylistResponse[] | null;
  playlist: PlaylistResponse | null;
}

const initialState: State = {
  product: null,
  productMedia: null,
  options: null,
  studioInstructors: null,
  productMediaDetail: null,
  teacherProducts: null,
  teacherProduct: null,
  teacherProductsWithPrivateSessions: [],
  teacherTaxRates: null,
  productTags: null,
  teacherCoupons: null,
  teacherCoupon: null,
  teacherCouponsStats: null,
  teacherProductsFilter: {
    q: null,
    productState: null,
  },
  productForm: null,
  playlists: [],
  playlist: null,
};

const productModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    product: state => state.product,
    productId: state => (state.product ? state.product.id : null),
    productType: state => (state.product ? state.product.product_type : null),
    productTitle: state => (state.product ? state.product.product_name : null),
    productDesciption: state =>
      state.product ? state.product.product_description : null,
    productDesciptionNew: state =>
      state.product ? state.product.product_description_new : null,
    productImage: state =>
      state.product && state.product.cover_file
        ? state.product.cover_file.storage_url
        : null,
    productInstructors: state =>
      state.product ? state.product.instructors : null,
    productLessons: state => (state.product ? state.product.lessons : null),
    productMedia: state => state.productMedia,
    productCommentsEnabled: state => state.product?.comments_enabled,
    previousProductMediaId: state => currentProductMediaId => {
      if (!currentProductMediaId || !state.productMedia) return;
      const indexOfCurrentProductMedia = state.productMedia.findIndex(
        media => media.id == currentProductMediaId
      );
      const previousProductMediaIndex =
        (indexOfCurrentProductMedia + state.productMedia.length - 1) %
        state.productMedia.length;
      return state.productMedia[previousProductMediaIndex]?.id;
    },
    nextProductMediaId: state => currentProductMediaId => {
      if (!currentProductMediaId || !state.productMedia) return;
      const indexOfCurrentProductMedia = state.productMedia.findIndex(
        media => media.id == currentProductMediaId
      );
      const nextProductMediaIndex =
        (indexOfCurrentProductMedia + 1) % state.productMedia.length;
      return state.productMedia[nextProductMediaIndex]?.id;
    },
    productContentById: state => (id: number) =>
      state.product ? state.product.lessons.find(l => l.id == id) : null,
    productMediaDetail: state =>
      state.productMediaDetail ? state.productMediaDetail : null,
    productMediaFilterOptions: state => state.options,
    studioInstructors: state => state.studioInstructors,
    hasProductMediaNextPage: state => state.productMediaNextPage != undefined,
    productEvents: state => (state.product ? state.product.events : null),
    teacherProducts: state => {
      if (!state.teacherProducts) return state.teacherProducts;
      const q = state.teacherProductsFilter.q?.toLowerCase();
      let results = state.teacherProducts.filter(p =>
        p.product_name.toLowerCase().includes(q ?? '')
      );
      const productState = state.teacherProductsFilter.productState;
      if (productState)
        results = results.filter(p => p.productState === productState);
      return results;
    },
    teacherProductsExcludeTypePunchCard: (state, getters) => {
      if (!state.teacherProducts) return [];
      return getters['allActiveTeacherProducts'].filter(
        p => p.product_type !== 'punch_card'
      );
    },
    teacherProductsExcludeTypePunchCardAndPrivateSpaces: (state, getters) => {
      if (!state.teacherProducts) return [];
      return getters['allActiveTeacherProducts'].filter(
        p =>
          p.product_type !== 'punch_card' && p.product_type !== 'private_space'
      );
    },
    teacherProduct: state => state.teacherProduct,
    teacherTaxRates: state => state.teacherTaxRates,
    productTags: state => state.productTags,
    teacherCouponsStats: state => state.teacherCouponsStats,
    teacherCoupon: state => state.teacherCoupon,
    productForm: state => state.productForm,
    allTeacherProducts: state => state.teacherProducts,
    allActiveTeacherProducts: state =>
      (state.teacherProducts || []).filter(p => !p.archived),
    allUpsellableTeacherProducts: (state, getters) =>
      getters.allActiveTeacherProducts.filter(
        p => p.product_type !== 'private_space'
      ),
    teacherProductsWithPrivateSessions: state =>
      (state.teacherProductsWithPrivateSessions || []).filter(p => !p.archived),
    bundlableProducts: state =>
      (state.teacherProducts || []).filter(
        p =>
          [
            'media_library',
            'course',
            'event_only',
            'punch_card',
            'private_coaching',
            'digital_download',
          ].indexOf(p.product_type) !== -1 && p.published
      ),
    productsCanAddMediaTo: state =>
      (state.teacherProducts || []).filter(
        p =>
          ['media_library', 'private_coaching', 'private_space'].indexOf(
            p.product_type
          ) !== -1 && p.published
      ),
    canViewProductContent: state => state.product?.can_view_content.can_view,
    cantViewProductContentReason: state =>
      state.product?.can_view_content.reason,
    productSalesPage: state => state.teacherProduct?.sales_page,
    salesPageItems: state => {
      const salesPageItemsKeys = [
        'introduction',
        'solution',
        'sign_up_banner',
        'testimonial',
        'instructors',
        'results',
        'bonus',
        'faq',
        'included_content',
        'introduction',
        'included_events',
        'included_lessons',
      ];

      return flow(
        sales_page => pick(sales_page, salesPageItemsKeys),
        sales_page =>
          Object.keys(sales_page).map(key => ({ key, data: sales_page[key] })),
        items => items.filter(item => item.data?.enabled),
        items => items.map(item => cloneDeep(item)),
        items => sortBy(items, 'data.position')
      )(state.product?.sales_page);
    },
    playlists: state => state.playlists,
    playlist: state => state.playlist,
  },
  actions: {
    // student
    async getProductDetail({ commit }, { studioURL, productId }) {
      commit(types.SET_PRODUCT, null);
      const product = await api.getProductDetail(studioURL, productId);
      commit(types.SET_PRODUCT, product);
      return product;
    },
    async fetchProductDetail({ commit }, { studioURL, productId }) {
      const product = await api.getProductDetail(studioURL, productId);
      return product;
    },
    async getProductForPurchase(_, { studioURL, productId }) {
      return await api.getProductForPurchase(studioURL, productId);
    },
    async refreshProductDetail({ commit }, { studioURL, productId }) {
      const product = await api.getProductDetail(studioURL, productId);
      commit(types.SET_PRODUCT, product);
    },
    async purchaseFreeProduct(_, { studioURL, productId, reCaptchaToken }) {
      const product = await api.purchaseFreeProduct(
        studioURL,
        productId,
        reCaptchaToken
      );
      return product;
    },
    async purchaseProduct({ dispatch }, payload) {
      const {
        coupon,
        studioURL,
        productId,
        recurringOption,
        daypass,
        isPaymentPlan,
        paymentPlanId,
        customAmount,
        useSavedCard,
        reCaptchaToken,
        rewardfulReferralId,
      } = payload;
      const product = await api.purchaseProduct(
        coupon,
        studioURL,
        productId,
        recurringOption,
        daypass,
        isPaymentPlan,
        reCaptchaToken,
        paymentPlanId,
        customAmount,
        useSavedCard,
        rewardfulReferralId
      );
      if (['punch_card', 'bundle'].indexOf(product.product_type) !== -1) {
        dispatch('user/studentPunches', null, { root: true });
      }
      return product;
    },
    async purchaseUpsellProduct({ dispatch }, payload) {
      const {
        studioURL,
        productId,
        upsellProductType,
        rewardfulReferralId,
      } = payload;

      const product = await api.purchaseUpsellProduct(studioURL, productId);
      if (['punch_card', 'bundle'].indexOf(upsellProductType) !== -1) {
        dispatch('user/studentPunches', null, { root: true });
      }
      return product;
    },
    async purchaseProductAsGift({ dispatch }, payload) {
      const {
        defaultPaymentMethod,
        stripeCustomerId,
        coupon,
        studioURL,
        productId,
        recurringOption,
        daypass,
        isPaymentPlan,
        reCaptchaToken,
        paymentPlanId,
        customAmount,
        giftPayload,
        rewardfulReferralId,
      } = payload;
      const product = await api.purchaseProductAsGift(
        defaultPaymentMethod,
        stripeCustomerId,
        coupon,
        studioURL,
        productId,
        recurringOption,
        daypass,
        isPaymentPlan,
        reCaptchaToken,
        paymentPlanId,
        customAmount,
        giftPayload,
        rewardfulReferralId
      );
      return product;
    },
    async validateStudentCoupon(_, { studioURL, productId, couponCode }) {
      const product = await api.validateStudentCoupon(
        studioURL,
        productId,
        couponCode
      );
      return product;
    },
    async verifyIntent(
      _,
      { studioURL, productId, paymentIntentId, setupIntentId }
    ) {
      await api.verifyIntent(
        studioURL,
        productId,
        paymentIntentId,
        setupIntentId
      );
    },
    async createStripeSetupIntent(_, { studioURL, credentials }) {
      const setupIntent = await api.createStripeSetupIntent(
        studioURL,
        credentials
      );
      return setupIntent;
    },
    async getStripeSetupIntent(_, { studioURL, params }) {
      const setupIntent = await api.getStripeSetupIntent(studioURL, params);
      return setupIntent;
    },
    async createGiftStripeSetupIntent(_, { studioURL, customerEmail }) {
      const setupIntent = await api.createGiftStripeSetupIntent(
        studioURL,
        customerEmail
      );
      return setupIntent;
    },
    async getProductMediaPaginated({ commit }, payload) {
      const {
        studioURL,
        productId,
        options,
        instructor,
        mediaType,
        q,
        page,
      } = payload;
      const media = await api.getProductMediaPaginated(
        studioURL,
        productId,
        options,
        instructor,
        mediaType,
        q,
        page
      );
      commit(types.SET_PRODUCT_MEDIA, media);
      return media;
    },
    async getProductMedia({ commit }, payload) {
      const {
        studioURL,
        productId,
        options,
        instructor,
        mediaType,
        q,
      } = payload;
      const media = await api.getProductMedia(
        studioURL,
        productId,
        options,
        instructor,
        mediaType,
        q
      );
      commit(types.SET_PRODUCT_MEDIA, media);
      return media;
    },
    async loadMoreProductMedia({ commit, state }, payload) {
      if (!state.productMediaNextPage) throw 'Empty page';
      const {
        studioURL,
        productId,
        options,
        instructor,
        mediaType,
        q,
      } = payload;
      const media = await api.getProductMedia(
        studioURL,
        productId,
        options,
        instructor,
        mediaType,
        q,
        state.productMediaNextPage
      );
      commit(types.APPEND_PRODUCT_MEDIA, media);
      return media;
    },
    async getProductMediaDetail(_, { studioURL, productId, mediaId }) {
      return await api.getProductMediaDetail(studioURL, productId, mediaId);
    },
    async getProductPlaylistMediaDetail(
      _,
      { studioURL, productId, playlistId, mediaId }
    ) {
      return await api.getProductPlaylistMediaDetail(
        studioURL,
        productId,
        playlistId,
        mediaId
      );
    },
    async getWaitlistEmails(_, payload) {
      const { productId, page } = payload;
      return await api.getWaitlistEmails(productId, page);
    },
    async exportWaitlistEmails(_, productId) {
      const data = await api.exportWaitlistEmails(productId);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'waitlist-email-export.csv');
    },
    async clearWaitlistEmails(_, productId) {
      return await api.clearWaitlistEmails(productId);
    },
    async getStudentPlaylists({ commit }, payload) {
      const { studioURL, productId } = payload;
      const playlists = await api.getStudentPlaylists(studioURL, productId);
      commit(types.SET_STUDENT_PLAYLISTS, playlists);
      return playlists;
    },
    async getStudentPlaylist({ commit }, payload) {
      const { studioURL, productId, playlistId } = payload;
      const playlist = await api.getStudentPlaylist(
        studioURL,
        productId,
        playlistId
      );
      commit(types.SET_STUDENT_PLAYLIST, playlist);
      return playlist;
    },
    async getStudentProductsReviews(_, payload) {
      const { studioURL, productId, page } = payload;
      const reviews = await api.getStudentProductsReviews(
        studioURL,
        productId,
        page
      );
      return reviews;
    },
    async studentSubmitReview(_, payload) {
      const { studioURL, productId, review, rating } = payload;
      await api.studentSubmitReview(studioURL, productId, review, rating);
    },
    async trackProductView(_, { studioURL, productId }) {
      await api.trackProductView(studioURL, productId);
    },
    async checkLessonAsCompleted({ commit }, payload) {
      const { studioURL, productId, lessonId } = payload;
      await api.checkLessonAsCompleted(studioURL, productId, lessonId);
      commit(types.SET_LESSONS_COMPLETE, lessonId);
    },
    async checkLessonAsIncompleted({ commit }, payload) {
      const { studioURL, productId, lessonId } = payload;
      await api.checkLessonAsIncompleted(studioURL, productId, lessonId);
      commit(types.SET_LESSONS_INCOMPLETE, lessonId);
    },
    async likeMedia(_, payload): Promise<boolean> {
      const { studioURL, productId, mediaId } = payload;
      return await api.likeMedia(studioURL, productId, mediaId);
    },
    async getFilterCategories({ commit }, payload) {
      const { studioURL } = payload;
      const options = await api.getFilterCategories(studioURL);
      commit(types.SET_FILTER_OPTIONS, options);
    },
    async getStudioInstructors({ commit }, payload) {
      const { studioURL } = payload;
      const instructors = await api.getStudioInstructors(studioURL);
      commit(types.SET_STUDIO_INSTRUCTORS, instructors);
    },
    async getStudioInstructor({ commit }, payload) {
      const { studioURL, instructorId } = payload;
      const instructor = await api.getStudioInstructor(studioURL, instructorId);
      return instructor;
    },
    resetState({ commit }) {
      commit(types.RESET_PRODUCT_STATE);
    },
    // teacher
    async setTeacherProducts({ commit }, teacherProducts: ProductResponse[]) {
      commit(types.SET_TEACHER_PRODUCTS, teacherProducts);
    },
    async getTeacherProducts({ commit }): Promise<void> {
      const result = await api.getTeacherProducts();
      commit(types.SET_TEACHER_PRODUCTS, result.map(formatProduct));
    },
    async getTeacherProductsWithPrivateSessions({ commit }): Promise<void> {
      const result = await api.getTeacherProductsWithPrivateSessions();
      commit(
        types.SET_TEACHER_PRODUCTS_WITH_PRIVATE_SESSIONS,
        result.map(r => formatProduct(r))
      );
    },
    async getTeacherProductDetail(
      { commit },
      id: number
    ): Promise<ProductResponse> {
      commit(types.SET_TEACHER_PRODUCT, null);
      const result = await api.getTeacherProductDetail(id);
      const product = formatProduct(result);
      commit(types.SET_TEACHER_PRODUCT, product);
      return product;
    },
    async updateTeacherProductDetail(
      { commit },
      id: number
    ): Promise<ProductResponse> {
      const result = await api.getTeacherProductDetail(id);
      const product = formatProduct(result);
      commit(types.SET_TEACHER_PRODUCT, product);
      return product;
    },
    async createProduct(
      _,
      data: CreateProductRequest
    ): Promise<ProductResponse> {
      const result = await api.createProduct(data);
      return result;
    },
    async updateProduct(
      _,
      data: UpdateProductRequest
    ): Promise<ProductResponse> {
      const result = await api.updateProduct(data);
      return result;
    },
    async deleteProduct(_, id: number): Promise<void> {
      await api.deleteProduct(id);
    },
    async duplicateProduct(_, id: number): Promise<void> {
      await api.duplicateProduct(id);
    },
    async moveCustomersToProduct(_, { sourceProductId, targetProductId }) {
      await api.moveCustomersToProduct(sourceProductId, targetProductId);
    },
    async getPendingContracts(_, { studioURL, productId }) {
      return await api.getPendingContracts(studioURL, productId);
    },
    async getProductGifts({ commit }, params) {
      const { page, q, startDate, endDate, id, status } = pick(params, [
        'page',
        'q',
        'startDate',
        'endDate',
        'id',
        'status',
      ]);

      const data = await api.getProductGifts(
        id,
        page,
        q,
        startDate,
        endDate,
        status
      );

      return data;
    },
    async getTeacherTaxRates({ commit }): Promise<TeacherTaxRateResponse[]> {
      const result = await api.getTeacherTaxRates();
      commit(types.SET_TEACHER_TAX_RATES, result);
      return result;
    },
    async addTeacherTaxRate(
      _,
      taxRate: TeacherTaxRateResponse
    ): Promise<TeacherTaxRateResponse> {
      const result = await api.addTeacherTaxRate(taxRate);
      return result;
    },
    async updateTeacherTaxRate(
      _,
      taxRate: TeacherTaxRateResponse
    ): Promise<TeacherTaxRateResponse> {
      const result = await api.updateTeacherTaxRate(taxRate);
      return result;
    },
    async getProductTags({ commit }): Promise<TeacherProductTagResponse[]> {
      const result = await api.getProductTags();
      commit(types.SET_PRODUCT_TAGS, result);
      return result;
    },
    async addProductTag(
      _,
      tag: TeacherProductTagResponse
    ): Promise<TeacherProductTagResponse> {
      const result = await api.addProductTag(tag);
      return result;
    },
    async updateProductTag(
      _,
      taxRate: TeacherProductTagResponse
    ): Promise<TeacherProductTagResponse> {
      const result = await api.updateProductTag(taxRate);
      return result;
    },
    async deleteProductTag(_, productTagId: number): Promise<void> {
      const result = await api.deleteProductTag(productTagId);
      return result;
    },
    async getTeacherCoupons(
      { commit },
      { page, q, is_active }
    ): Promise<PaginatedResponse<TeacherCouponsResponse>> {
      const result = await api.getTeacherCoupons(page, q, is_active);
      commit(types.SET_TEACHER_COUPONS, result);
      return result;
    },
    async getTeacherCouponDetail(
      { commit },
      id: number
    ): Promise<TeacherCouponsResponse> {
      const result = await api.getTeacherCouponDetail(id);
      commit(types.SET_TEACHER_COUPON, result);
      return result;
    },
    async getTeacherCouponsStats({ commit }): Promise<void> {
      const result = await api.getTeacherCouponsStats();
      commit(types.SET_TEACHER_COUPONS_STATS, result);
    },
    async createCoupon(
      _,
      data: CreateCouponRequest
    ): Promise<TeacherCouponsResponse> {
      const result = await api.createCoupon(data);
      return result;
    },
    async updateCoupon(
      _,
      data: UpdateCouponRequest
    ): Promise<TeacherCouponsResponse> {
      const result = await api.updateCoupon(data);
      return result;
    },
    async deleteCoupon(_, id: number): Promise<void> {
      await api.deleteCoupon(id);
    },
    setTeacherProductsFilter({ commit }, payload: ProductsFilter | null): void {
      commit(types.SET_FILTER, payload);
    },
    async getCouponsUsage(_, id: number): Promise<CouponUsageResponse[]> {
      return await api.getCouponsUsage(id);
    },
    async getProductPurchases(
      _,
      { id, page, q, dateFrom, dateTo }
    ): Promise<PaginatedResponse<ProductStudentCustomer>> {
      const result = await api.getProductPurchases(
        id,
        page,
        q,
        dateFrom,
        dateTo
      );
      return result;
    },
    async getProductCustomers(
      _,
      { id, page, q, dateFrom, dateTo }
    ): Promise<PaginatedResponse<ProductStudentCustomer>> {
      const result = await api.getProductCustomers(
        id,
        page,
        q,
        dateFrom,
        dateTo
      );
      return result;
    },
    async getProductEvents(
      _,
      { id, page, q, dateFrom, dateTo, ordering }
    ): Promise<PaginatedResponse<EventResponse[]>> {
      const result = await api.getProductEvents(
        id,
        page,
        q,
        dateFrom,
        dateTo,
        ordering
      );
      return result;
    },
    async getProductPrivateSpace(
      _,
      { id, page, q, dateFrom, dateTo, ordering }
    ): Promise<PaginatedResponse<any[]>> {
      const result = await api.getProductPrivateSpace(
        id,
        page,
        q,
        dateFrom,
        dateTo,
        ordering
      );
      return result;
    },
    async getProductReviews(
      _,
      { id, page, q, dateFrom, dateTo, ordering }
    ): Promise<PaginatedResponse<ProductReviewResponse[]>> {
      const result = await api.getProductReviews(
        id,
        page,
        q,
        dateFrom,
        dateTo,
        ordering
      );
      return result;
    },
    async hideProductReview(_, { productId, reviewId }): Promise<void> {
      const result = await api.hideProductReview(productId, reviewId);
      return result;
    },
    async unhideProductReview(_, { productId, reviewId }): Promise<void> {
      const result = await api.unhideProductReview(productId, reviewId);
      return result;
    },
    async registerProductCustomersToEvent(
      _,
      { productId, eventId }
    ): Promise<void> {
      const result = await api.registerProductCustomersToEvent(
        productId,
        eventId
      );
      return result;
    },
    setProductForm({ commit }, data: ProductForm): void {
      commit(types.SET_PRODUCT_FORM, data);
    },
    setProductFormPublished({ commit }, published: boolean): void {
      const data: { published: boolean; visible?: boolean } = {
        published: published,
      };
      if (!published) {
        data.visible = false;
      }
      commit(types.SET_PRODUCT_FORM, data);
    },
    setProductFormPrivateSessions(
      { commit },
      has_private_sessions: boolean
    ): void {
      const data: {
        has_private_sessions: boolean;
        calendly_enabled?: boolean;
        acuity_enabled?: boolean;
      } = {
        has_private_sessions: has_private_sessions,
      };
      if (!has_private_sessions) {
        data.calendly_enabled = false;
        data.acuity_enabled = false;
      }
      commit(types.SET_PRODUCT_FORM, data);
    },
    setProductFormCalendly({ commit }, calendly_enabled: boolean): void {
      const data: {
        calendly_enabled?: boolean;
        acuity_enabled?: boolean;
      } = { calendly_enabled: calendly_enabled };
      if (calendly_enabled) {
        data.acuity_enabled = false;
      }
      commit(types.SET_PRODUCT_FORM, data);
    },
    setProductFormAcuity({ commit }, acuity_enabled: boolean): void {
      const data: {
        calendly_enabled?: boolean;
        acuity_enabled?: boolean;
      } = { acuity_enabled: acuity_enabled };
      if (acuity_enabled) {
        data.calendly_enabled = false;
      }
      commit(types.SET_PRODUCT_FORM, data);
    },
    async getProductStudents(_, productId: number): Promise<StudentResponse[]> {
      return await api.getProductStudents(productId);
    },
    async createPlaylist(
      _,
      { productId, playlist }
    ): Promise<PlaylistResponse> {
      return await api.createPlaylist(productId, playlist);
    },
    async updatePlaylist(
      _,
      { productId, playlist }
    ): Promise<PlaylistResponse> {
      return await api.updatePlaylist(productId, playlist);
    },
    async deletePlaylist(_, { productId, playlistId }): Promise<void> {
      await api.deletePlaylist(productId, playlistId);
    },
    async getPlaylists(_, productId): Promise<PlaylistResponse[]> {
      return await api.getPlaylists(productId);
    },
    async createProductLesson(_, { productId, data }): Promise<LessonResponse> {
      return await api.createLesson(productId, data);
    },
    async updateProductLesson(_, data: LessonRequest): Promise<LessonResponse> {
      return await api.updateLesson(data.product, data);
    },
    async removeProductLessonContent(
      _,
      { productId, lessonId, contentId }
    ): Promise<void> {
      return await api.removeLessonContent(productId, lessonId, contentId);
    },
    async reorderProductLesson(
      _,
      { productId, lessonId, content }
    ): Promise<ReorderContentResponse[]> {
      return await api.reorderLesson(productId, lessonId, content);
    },
    async deleteProductLesson(_, data: LessonRequest): Promise<void> {
      await api.deleteLesson(data.product, data.id);
    },
    async getProductContent(
      _,
      { productId, page, fileType, instructor, options, q }
    ): Promise<PaginatedResponse<ContentResponse>> {
      return await api.getProductContent(
        productId,
        page,
        fileType,
        instructor,
        options,
        q
      );
    },
    async getIncludedProducts(_, productId): Promise<ProductResponse[]> {
      return await api.getIncludedProducts(productId);
    },
    async addIncludedProducts(
      _,
      { productId, data }
    ): Promise<ProductResponse[]> {
      return await api.addIncludedProducts(productId, data);
    },
    async reorderIncludedProducts(
      _,
      { bundleId, products }
    ): Promise<ProductResponse[]> {
      return await api.reorderIncludedProducts(bundleId, products);
    },
    async reorderProducts(
      _,
      products: { id: number; position: number }[]
    ): Promise<{ id: number; position: number }[]> {
      return await api.reorderProducts(products);
    },
    async reorderPlaylists(
      _,
      { productId, playlists }
    ): Promise<{ id: number; position: number }[]> {
      return await api.reorderPlaylists(productId, playlists);
    },
    async reorderLessons(
      _,
      { productId, lessons }
    ): Promise<{ id: number; position: number }[]> {
      return await api.reorderLessons(productId, lessons);
    },
    async deletePlaylistContent(
      _,
      { productId, playlistId, contentId }
    ): Promise<void> {
      return await api.deletePlaylistContent(productId, playlistId, contentId);
    },
    async deleteIncludedProduct(_, { bundleId, productId }): Promise<void> {
      await api.deleteIncludedProduct(bundleId, productId);
    },
    async createProductContent(
      _,
      { productId, data }
    ): Promise<PaginatedResponse<ContentResponse>> {
      return await api.createProductContent(productId, data);
    },
    async deleteProductContent(_, { productId, contentId }): Promise<void> {
      await api.deleteProductContent(productId, contentId);
    },
    async reorderPlaylistContent(
      _,
      { productId, playlistId, content }
    ): Promise<ReorderContentResponse[]> {
      return await api.reorderPlaylistContent(productId, playlistId, content);
    },
    async reorderProductContent(
      _,
      { productId, content }
    ): Promise<ReorderContentResponse[]> {
      return await api.reorderProductContent(productId, content);
    },
    async createProductSalesPage(_, data) {
      const { product, is_simple } = pick(data, ['product', 'is_simple']);
      return await api.createProductSalesPage(product, {
        is_simple,
      });
    },
    async updateProductSalesPage(_, data) {
      const { product, id, is_simple } = pick(data, [
        'product',
        'id',
        'is_simple',
      ]);
      return await api.updateProductSalesPage(product, id, {
        is_simple,
      });
    },
    async createSalePageSection(
      _,
      data
    ): Promise<ProductSalesPageSectionResponse> {
      const { product, salesPage, section, payload } = pick(data, [
        'product',
        'salesPage',
        'section',
        'payload',
      ]);
      return await api.createSalePageSection(
        product,
        salesPage,
        section,
        payload
      );
    },
    async updateSalePageSection(
      _,
      data
    ): Promise<ProductSalesPageSectionResponse> {
      const { product, salesPage, section, payload } = pick(data, [
        'product',
        'salesPage',
        'section',
        'payload',
      ]);
      return await api.updateSalePageSection(
        product,
        salesPage,
        section,
        payload.id,
        payload
      );
    },
    async canAddPrivateSession(
      _,
      { studioURL, productId }
    ): Promise<CanAddPrivateSessionResponse> {
      return await api.canAddPrivateSession(studioURL, productId);
    },
    async getPrivateSessions(
      _,
      { studioURL, productId }
    ): Promise<EventResponse[]> {
      return await api.getPrivateSessions(studioURL, productId);
    },
    async getPrivateSchedulingTimeSlots(
      _,
      { studioURL, productId, startDate, endDate, timezone }
    ): Promise<any> {
      return await api.getPrivateSchedulingTimeSlots(
        studioURL,
        productId,
        startDate,
        endDate,
        timezone
      );
    },
    async bookPrivateSchedulingSlot(
      _,
      { studioURL, productId, slot, timezone }
    ): Promise<any> {
      return await api.bookPrivateSchedulingSlot(
        studioURL,
        productId,
        slot,
        timezone
      );
    },
    async createProductsSalesPageFAQ(
      _,
      data
    ): Promise<ProductSalesPageFAQResponse> {
      const { productId, salesPageId, faqsId, payload } = pick(data, [
        'productId',
        'salesPageId',
        'faqsId',
        'payload',
      ]);
      return await api.createProductsSalesPageFAQ(
        productId,
        salesPageId,
        faqsId,
        payload
      );
    },
    async updateProductsSalesPageFAQ(
      _,
      data
    ): Promise<ProductSalesPageFAQResponse> {
      const { productId, salesPageId, faqsId, faqId, payload } = pick(data, [
        'productId',
        'salesPageId',
        'faqsId',
        'faqId',
        'payload',
      ]);
      return await api.updateProductsSalesPageFAQ(
        productId,
        salesPageId,
        faqsId,
        faqId,
        payload
      );
    },
    async removeProductsSalesPageFAQ(_, data): Promise<void> {
      const { productId, salesPageId, faqsId, faqId } = pick(data, [
        'productId',
        'salesPageId',
        'faqsId',
        'faqId',
      ]);
      await api.removeProductsSalesPageFAQ(
        productId,
        salesPageId,
        faqsId,
        faqId
      );
    },
    async createProductsSalesPageTestimonial(
      _,
      data
    ): Promise<ProductSalesPageTestimonialResponse> {
      const { productId, salesPageId, testimonialsId, payload } = pick(data, [
        'productId',
        'salesPageId',
        'testimonialsId',
        'payload',
      ]);
      return await api.createProductsSalesPageTestimonial(
        productId,
        salesPageId,
        testimonialsId,
        payload
      );
    },
    async updateProductsSalesPageTestimonial(
      _,
      data
    ): Promise<ProductSalesPageTestimonialResponse> {
      const {
        productId,
        salesPageId,
        testimonialsId,
        testimonialId,
        payload,
      } = pick(data, [
        'productId',
        'salesPageId',
        'testimonialsId',
        'testimonialId',
        'payload',
      ]);
      return await api.updateProductsSalesPageTestimonial(
        productId,
        salesPageId,
        testimonialsId,
        testimonialId,
        payload
      );
    },
    async removeProductsSalesPageTestimonial(_, data): Promise<void> {
      const {
        productId,
        salesPageId,
        testimonialsId,
        testimonialId,
      } = pick(data, [
        'productId',
        'salesPageId',
        'testimonialsId',
        'testimonialId',
      ]);
      await api.removeProductsSalesPageTestimonial(
        productId,
        salesPageId,
        testimonialsId,
        testimonialId
      );
    },
    async exportTeacherProductPurchases(_, { product, q, dateFrom, dateTo }) {
      const data = await api.exportTeacherProductPurchases(
        product,
        q,
        dateFrom,
        dateTo
      );
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'purchases-export.csv');
    },
    async exportTeacherProductCustomers(_, { product, q }) {
      const data = await api.exportTeacherProductCustomers(product, q);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'customers-export.csv');
    },
    async exportTeacherProductVideoProgress(_, { product, name }) {
      const data = await api.exportTeacherProductVideoProgress(product);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const slug = name ? slugify(name) : `product-${product}`;
      saveAs(blob, `${slug}-video-progress.csv`);
    },
    async addPaymentPlan(
      _,
      { productId, paymentPlan }
    ): Promise<PaymentPlanResponse> {
      return await api.addPaymentPlan(productId, paymentPlan);
    },
    async updatePaymentPlan(
      _,
      { productId, paymentPlan }
    ): Promise<PaymentPlanResponse> {
      return await api.updatePaymentPlan(productId, paymentPlan);
    },
    async removePaymentPlan(_, { productId, paymentPlan }): Promise<void> {
      return await api.removePaymentPlan(productId, paymentPlan);
    },
    async awardFreeClassCardRedemptions(
      { commit },
      { productId }
    ): Promise<void> {
      const result = await api.awardFreeClassCardRedemptions(productId);
      return result;
    },
    async emailCustomers(
      { commit },
      { productId, subject, body }
    ): Promise<void> {
      const result = await api.emailCustomers(productId, subject, body);
      return result;
    },
    async generateProductPage({ commit }, { productId, tone }): Promise<void> {
      const result = await api.generateProductPage(productId, tone);
      return result;
    },
  },
  mutations: {
    [types.SET_PRODUCT](state: State, product: ProductDetailResponse) {
      state.product = product;
    },
    [types.UPDATE_LESSON_QUIZ_STATUS](state: State, { lesson_id, status }) {
      const lesson = state.product?.lessons.find(
        lesson => lesson.id === lesson_id
      );
      if (lesson) {
        lesson.quiz_status = status;
      }
    },
    [types.SET_STUDENT_PLAYLISTS](state: State, data: PlaylistResponse[]) {
      state.playlists = data;
    },
    [types.SET_STUDENT_PLAYLIST](state: State, data: PlaylistResponse) {
      state.playlist = data;
    },
    [types.SET_PRODUCT_MEDIA](
      state: State,
      productMedia: PaginatedResponse<ProductMediaResponse>
    ) {
      state.productMedia = productMedia.results;
      state.productMediaNextPage = productMedia.next;
    },
    [types.APPEND_PRODUCT_MEDIA](
      state: State,
      productMedia: PaginatedResponse<ProductMediaResponse>
    ) {
      if (state.productMedia) {
        state.productMedia = [...state.productMedia, ...productMedia.results];
      }
      state.productMediaNextPage = productMedia.next;
    },
    [types.SET_LESSONS_COMPLETE](state: State, lessonId: number) {
      if (state.product) {
        const lessons = state.product.lessons;
        const lesson = lessons.find(l => l.id == lessonId);
        if (lesson) {
          lesson.completed = true;
          const nextLessonIndex = lessons.indexOf(lesson) + 1;
          if (nextLessonIndex < lessons.length) {
            const nextLesson = lessons[nextLessonIndex];
            if (
              nextLesson &&
              nextLesson.locked &&
              nextLesson.lesson_start_type ===
                LESSON_START_TYPE_AFTER_PREVIOUS_LESSON
            ) {
              nextLesson.locked = false;
            }
          }
        }
      }
    },
    [types.SET_LESSONS_INCOMPLETE](state: State, lessonId: number) {
      if (state.product) {
        const lessons = state.product.lessons;
        const lesson = lessons.find(l => l.id == lessonId);
        if (lesson) {
          lesson.completed = false;
          const nextLessonIndex = lessons.indexOf(lesson) + 1;
          if (nextLessonIndex < lessons.length) {
            const nextLesson = lessons[nextLessonIndex];
            if (
              nextLesson &&
              !nextLesson.locked &&
              nextLesson.lesson_start_type ===
                LESSON_START_TYPE_AFTER_PREVIOUS_LESSON
            ) {
              nextLesson.locked = true;
            }
          }
        }
      }
    },
    [types.SET_FILTER_OPTIONS](
      state: State,
      options: FilterCategoryResponse[]
    ) {
      state.options = options.sort(o => o.position);
    },
    [types.SET_STUDIO_INSTRUCTORS](
      state: State,
      instructors: InstructorResponse[]
    ) {
      state.studioInstructors = instructors.sort(o => o.position);
    },
    [types.RESET_PRODUCT_STATE](state: State) {
      Object.assign(state, {}, initialState);
    },
    [types.SET_TEACHER_PRODUCTS](state: State, data: ProductResponse[]) {
      state.teacherProducts = data;
    },
    [types.SET_TEACHER_PRODUCT](
      state: State,
      data: ProductDetailResponse | null
    ) {
      state.teacherProduct = data;
    },
    [types.SET_TEACHER_PRODUCTS_WITH_PRIVATE_SESSIONS](
      state: State,
      data: ProductResponse[]
    ) {
      state.teacherProductsWithPrivateSessions = data;
    },
    [types.SET_TEACHER_TAX_RATES](
      state: State,
      data: TeacherTaxRateResponse[]
    ) {
      state.teacherTaxRates = data;
    },
    [types.SET_PRODUCT_TAGS](state: State, data: TeacherProductTagResponse[]) {
      state.productTags = data;
    },
    [types.SET_TEACHER_COUPONS](
      state: State,
      data: PaginatedResponse<TeacherCouponsResponse>
    ) {
      state.teacherCoupons = data;
    },
    [types.SET_TEACHER_COUPON](state: State, data: TeacherCouponsResponse) {
      state.teacherCoupon = data;
    },
    [types.SET_TEACHER_COUPONS_STATS](
      state: State,
      data: CouponsStatsResponse
    ) {
      state.teacherCouponsStats = data;
    },
    [types.SET_FILTER](state: State, payload: ProductsFilter | null) {
      if (payload) {
        const { q, productState } = payload;
        state.teacherProductsFilter = {
          q,
          productState,
        };
      } else {
        state.teacherProductsFilter = {
          q: null,
          productState: null,
        };
      }
    },
    [types.SET_PRODUCT_FORM](state: State, data: ProductForm) {
      if (data) state.productForm = { ...state.productForm, ...data };
      else state.productForm = null;
    },
  },
};

export default productModule;
